import React from "react";
import {Headings, Title} from "../Sections";
import styled from "styled-components";
import {scaledValue} from "../../performance/CompactPerformanceValue";
import PortfolioDescription from "./PortfolioDescription";
import {conditionalRequirementRow, requirementRow, stockRestrictionTreeRow} from "./RequirementRow";

const Container = styled.div`
    display: flex;
    flex-direction: column;
`;

const TitleContainer = styled(Headings)`
    flex-grow: 0;
    flex-shrink: 0;
    font-weight: normal;
    height: 32px;
`;

function pricingMethodFrom(pricingMethod) {
    switch (pricingMethod) {
        case null:
        case "TOUCH":
            return "Touch";
        case "END_OF_DAY":
            return "End of day";
        default:
            return pricingMethod;
    }
}

function benchmarkingDisplayFrom(benchmarking, globalBenchmark) {
    switch (benchmarking) {
        case "ContributorThenDefault":
            return `Author or ${globalBenchmark}`;
        case "LocalCountryThenDefault":
            return "Country";
        case "LocalSectorThenDefault":
            return "Sector";
        case "ContributorThenLocalCountryThenDefault":
            return "Author or Country";
        case "ContributorThenLocalSectorThenDefault":
            return "Author or Sector";
        default:
            return globalBenchmark;
    }
}

const valueRangeFor = (range, label, className) => {
    if (!range.maximum) {
        return requirementRow(className, label, `min ${scaledValue(range.minimum)}`);
    }
    if (!range.minimum) {
        return requirementRow(className, label, `max ${scaledValue(range.maximum)}`);
    }
    if (range.minimum === range.maximum) {
        return requirementRow(className, label, `fixed ${scaledValue(range.maximum)}`);
    }
    return requirementRow(className, label, `${scaledValue(range.minimum)} to ${scaledValue(range.maximum)}`);
};

const marketCapFrom = (marketCap) => valueRangeFor(marketCap, "Market cap", "t-market-cap");
const investmentPerIdeaFrom = (range, maxAdv) => {
    const className = "t-investment-per-idea";
    const label = "Investment per idea";
    if (!maxAdv) {
        return range ? valueRangeFor(range, label, className) : null;
    }
    const adv = `${(maxAdv * 100).toFixed(0)}% ADV`;
    if (!range) {
        return requirementRow(className, label, `max ${adv}`);
    }
    if (!range.maximum) {
        return requirementRow(className, label, `${scaledValue(range.minimum)} to ${adv}`)
    }
    if (!range.minimum) {
        return requirementRow(className, label, `max ${scaledValue(range.maximum)} and ${adv}`)
    }
    if (range.minimum === range.maximum) {
        return requirementRow(className, label, `fixed ${scaledValue(range.maximum)} max ${adv}`)
    }
    return requirementRow(className, label, `${scaledValue(range.minimum)} to ${scaledValue(range.maximum)} max ${adv}`);
};


const directionGateFrom = (limit) => {
    if (limit.longs === 0) {
        return "Short only";
    }
    if (limit.shorts === 0) {
        return "Long only";
    }
    return "";
};

const requiredFieldsFrom = (timeHorizonIsRequired, targetPriceIsRequired, commentaryIsRequired, minWordsInOpeningCommentary, minCharactersInOpeningCommentary) => {
    const reqs = [];
    if (targetPriceIsRequired) {
        reqs.push("Target price");
    }
    if (timeHorizonIsRequired) {
        reqs.push("Time horizon");
    }
    if (minCharactersInOpeningCommentary) {
        reqs.push(`Commentary (min ${minCharactersInOpeningCommentary} characters)`);
    } else if (minWordsInOpeningCommentary) {
        reqs.push(`Commentary (min ${minWordsInOpeningCommentary} words)`);
    } else if (commentaryIsRequired) {
        reqs.push("Commentary");
    }

    if (!reqs.length) {
        return null;
    }
    return requirementRow("t-required-fields", "Required fields", reqs.join(", "));
};

const filterChildren = (firstLevelNodes) => {
    if (!firstLevelNodes) return "";

    const leafNodes = [];
    firstLevelNodes.forEach(n => {
        if (n.nodes) {
            n.nodes.forEach(m => leafNodes.push(m));
        } else {
            leafNodes.push(n);
        }
    });

    const included = leafNodes.filter(i => i.included);
    const excluded = leafNodes.filter(i => !i.included);
    if (excluded.length === 0) return "";

    const showExcluded = excluded.length <= included.length;
    const labelPrefix = showExcluded ? "except " : "";
    const items = showExcluded ? excluded : included;

    return " (" + labelPrefix + items.map(item => item.id || item.name).join(", ") + ")";
};

const filterTree = (className, label, tree) => {
    const topLevelIncluded = tree.filter(i => i.included && i.name !== "UNKNOWN");
    return stockRestrictionTreeRow(className, "Allowed " + label, topLevelIncluded.map((item, i) => (<span key={label + i}>{i ? ", " : ""}<b>{item.name}</b>{filterChildren(item.nodes)}</span>)));
};

const SubsectionHeading = styled.div`
    padding-top: 8px;
`;

const requirementsSection = (items, heading) => {
    if (!items.length) {
        return null;
    }
    return (<div>
        <SubsectionHeading>{heading}</SubsectionHeading>
        <div>{items}</div>
    </div>);
};

export const PortfolioRequirements = ({portfolioName,
                                       portfolioCompanyId,
                                       portfolioCompanyName,
                                       authorCompanyId,
                                       description,
                                       requirements,
                                       performanceSettings,
                                       kpi,
                                       openIdeaCount,
                                       currentInvestment,
                                       showStatus,
                                       authorId}) => {
    const {
        commentaryIsRequired,
        timeHorizonIsRequired,
        targetPriceIsRequired,
        investmentPerIdea,
        maxAdv,
        minAdv,
        maxIdeasPerAuthor,
        maxIdeasPerCompany,
        maxInvestmentPerAuthor,
        maxRegionalInvestmentPerAuthor,
        maxInvestmentPerCompany,
        minWordsInOpeningCommentary,
        minCharactersInOpeningCommentary,
        marketCap,
        instrumentClassRestrictionTree,
        industryRestrictionTree,
        exchangeRestrictionTree,
        maxUniqueIdeasPerAuthor,
        maxIdeasPerTickerPerAuthor,
        minDaysBeforeBucketClosure,
        minTimeSinceLastClose,
        minTimeSinceLastChange,
        stockBlacklist,
        stockBlacklistForShortsOnly,
        autoCloseTriggers
    } = requirements;


    function allocation(limit, contributorLevel, used) {
        const allocations = [];
        if (limit.overall) {
            const status = showStatus && used ? " (" + scaledValue(limit.overall - used) + " available)" : "";
            allocations.push(requirementRow(`t-max-investment-per-${contributorLevel}`, `Capital allocation`, `${scaledValue(limit.overall)} per ${contributorLevel}${status}`));
        }
        const directionLimits = [];
        if (limit.longs) {
            directionLimits.push(`${scaledValue(limit.longs)} long`);
        }
        if (limit.shorts) {
            directionLimits.push(`${scaledValue(limit.shorts)} short`);
        }
        if (directionLimits.length) {
            allocations.push(requirementRow(`t-direction-allocation-per-${contributorLevel}`, "Direction allocation", `max ${directionLimits.join(" ")}`));
        }
        return allocations;
    }

    function regionalAllocation({asiaPac, emea, americas}, currentInvestment) {
        const limits = [];
        if (asiaPac) {
            const status = (currentInvestment && currentInvestment.asiaPac) ? " (" + scaledValue(asiaPac - currentInvestment.asiaPac) + " available)" : "";
            limits.push(requirementRow(`t-max-asiapac-investment-per-author`, "Max allocation in Asia Pacific", `${scaledValue(asiaPac)}${status}`));
        }
        if (emea) {
            const status = (currentInvestment && currentInvestment.emea) ? " (" + scaledValue(emea - currentInvestment.emea) + " available)" : "";
            limits.push(requirementRow(`t-max-emea-investment-per-author`, "Max allocation in EMEA", `${scaledValue(emea)}${status}`));
        }
        if (americas) {
            const status = (currentInvestment && currentInvestment.americas) ? " (" + scaledValue(americas - currentInvestment.americas) + " available)" : "";
            limits.push(requirementRow(`t-max-americas-investment-per-author`, "Max allocation in Americas", `${scaledValue(americas)}${status}`));
        }
        return limits;
    }

    function ideaLimits(limit, contributorLevel, used) {
        const allocations = [];
        if (limit.overall) {
            const status = showStatus && used ? " (" + (limit.overall - used) + " available)" : "";
            allocations.push(requirementRow(`t-max-ideas-per-${contributorLevel}`, "Open idea limit", `${limit.overall} per ${contributorLevel}${status}`));
        }

        const directionGate = directionGateFrom(limit);
        const directionLimits = [];
        if (limit.longs) {
            directionLimits.push(limit.longs);
            if (directionGate === "") {
                directionLimits.push(`long${limit.longs !== 1 ? "s" : ""}`);
            }
        }

        if (limit.shorts) {
            directionLimits.push(limit.shorts);
            if (directionGate === "") {
                directionLimits.push(`short${limit.shorts !== 1 ? "s" : ""}`);
            }
        }

        if (directionGate !== "") {
            directionLimits.unshift(directionGate);
            allocations.push(requirementRow(`t-idea-direction-limits-per-${contributorLevel}`, "Idea direction limits", `${directionLimits.join(" ")}${directionLimits.length > 1 ? " max": ""}`));
        } else if (directionLimits.length) {
            allocations.push(requirementRow(`t-idea-direction-limits-per-${contributorLevel}`, "Idea direction limits", `max ${directionLimits.join(" ")}`));
        }

        return allocations;
    }

    function ideaConstraints() {
        const items = [];
        if (maxIdeasPerAuthor) { items.push(ideaLimits(maxIdeasPerAuthor, "author", openIdeaCount)); }
        if (maxIdeasPerCompany) { items.push(ideaLimits(maxIdeasPerCompany, "company")); }
        if (maxInvestmentPerAuthor) { items.push(allocation(maxInvestmentPerAuthor, "author", currentInvestment.overall || 0)); }
        if (maxRegionalInvestmentPerAuthor) { items.push(regionalAllocation(maxRegionalInvestmentPerAuthor, currentInvestment)); }
        if (maxInvestmentPerCompany) { items.push(allocation(maxInvestmentPerCompany, "company")); }

        const investment = investmentPerIdeaFrom(investmentPerIdea, maxAdv);
        if (investment !== null) { items.push(investment) }
        const required = requiredFieldsFrom(timeHorizonIsRequired, targetPriceIsRequired, commentaryIsRequired, minWordsInOpeningCommentary, minCharactersInOpeningCommentary);
        if (required !== null) { items.push(required) }

        return requirementsSection(items, "Idea entry");
    }

    function stockSelection() {
        const items = [];

        if ((maxUniqueIdeasPerAuthor !== 1 && maxIdeasPerTickerPerAuthor !== 1)) {
            items.push(requirementRow("t-idea-non-uniqueness", "Multiple ideas per stock", "allowed"));
        }
        if (marketCap) {
            items.push(marketCapFrom(marketCap));
        }
        if (minAdv) {
            items.push(requirementRow("t-min-adv", "Minimum ADV", Number(minAdv).toLocaleString()))
        }
        if (instrumentClassRestrictionTree) {
            items.push(filterTree("t-instrument-classes", "classes", instrumentClassRestrictionTree));
        }
        if (industryRestrictionTree) {
            items.push(filterTree("t-sectors", "sectors", industryRestrictionTree));
        }
        if (exchangeRestrictionTree) {
            items.push(filterTree("t-exchanges", "exchanges", exchangeRestrictionTree));
        }
        if (stockBlacklist) {
            items.push(requirementRow("t-stocks", "Allowed stocks", "All except some stocks on " + stockBlacklist.map(item => item.name).sort().join(", ")));
        }
        if (stockBlacklistForShortsOnly) {
            items.push(requirementRow("t-stocks", "Allowed stocks", "All except shorts on some stocks on " + stockBlacklistForShortsOnly.map(item => item.name).sort().join(", ")));
        }

        return requirementsSection(items, "Stock selection");
    }

    function timing() {
        const items = [];

        if (minDaysBeforeBucketClosure) {
            const value = minDaysBeforeBucketClosure.value ? minDaysBeforeBucketClosure.value : minDaysBeforeBucketClosure;
            if (value) {
                const condition = minDaysBeforeBucketClosure.conditionalValue ? ` (unless lifetime relative return > ${(100 * minDaysBeforeBucketClosure.conditionalValue).toPrecision(2)}%)` : "";
                const description = <><b>{value} day{value !== 1 ? "s" : ""}</b><span>{condition}</span></>;
                items.push(conditionalRequirementRow("t-min-holding-period", "Min holding period", description));
            }
        }
        if (minTimeSinceLastClose) { items.push(requirementRow("t-min-time-since-last-close", "Min time since closing on same stock", `${minTimeSinceLastClose} hour${minTimeSinceLastClose !== 1 ? "s" : ""}`)); }
        if (minTimeSinceLastChange) { items.push(requirementRow("t-min-time-since-last-trade", "Min time since last trade", `${minTimeSinceLastChange} hour${minTimeSinceLastChange !== 1 ? "s" : ""}`)); }

        return requirementsSection(items, "Timing");
    }

    function autoCloses(ownFirm) {
        const items = [];

        if (autoCloseTriggers) {
            autoCloseTriggers.filter(forCompany => {
                if (ownFirm) {
                    return forCompany.companyId === authorCompanyId || forCompany.companyId !== portfolioCompanyId;
                }
                return forCompany.companyId === portfolioCompanyId && forCompany.companyId !== authorCompanyId;
            }).map(forCompany => {
                forCompany.triggers.map(t => {
                    if (t.affectedAuthors && !t.affectedAuthors.includes(authorId)) {
                        return;
                    }
                    const labelClass = "t-auto-close-" + (ownFirm ? "own-firm" : "client");
                    const messages = [];
                    let label = "Lifetime limit";
                    if (t.byDaysOpen) {
                        messages.push(`${t.byDaysOpen.days} days`);
                    }
                    if (t.byPerformance) {
                        const op = t.byPerformance.operator.includes("GREATER_THAN") ? "take profit above" : "stop loss below";
                        const ret = t.byPerformance.returnPercent * 100.0;
                        const dayCount = t.byPerformance.consecutiveDays > 1 ? ` for ${t.byPerformance.consecutiveDays} consecutive days` : "";
                        label = "Portfolio this period";
                        if (t.byPerformance.kpi.includes("Tim")) {
                            label = "Lifetime " + t.byPerformance.kpi.substr(3).toLowerCase() + " return";
                        }
                        label += ` to ${t.byPerformance.consecutiveDays ? "EoD" : "now"}`;
                        messages.push(`${op} ${ret}%${dayCount}`);
                    }
                    if (messages.length === 0) {
                        return;
                    }
                    items.push(requirementRow(labelClass, label, (messages.length === 2 ? `${messages[1]} if older than ${messages[0]}` : messages[0])));
                });
            });
        }
        return requirementsSection(items, `Auto-close (${ownFirm ? "your firm" : portfolioCompanyName})`);
    }

    function performance() {
        if (!performanceSettings) {
            return null;
        }
        const items = [];

        if (performanceSettings.currency) { items.push(requirementRow("t-currency", "Currency", performanceSettings.currency)); }
        items.push(requirementRow("t-pricing-method", "Pricing method", pricingMethodFrom(performanceSettings.pricingMethod)));
        if (performanceSettings.benchmarking) { items.push(requirementRow("t-benchmarking", "Benchmarking", benchmarkingDisplayFrom(performanceSettings.benchmarking, performanceSettings.globalBenchmark))); }

        if (kpi && kpi.includes("Percent")) {
            if (!performanceSettings.shareBased) {
                items.push(requirementRow("t-return-percent-basis", "Return % basis", "Price only"));
            } else if (performanceSettings.investmentAllocation) {
                items.push(requirementRow("t-return-percent-basis", "Summary return % denominator", scaledValue(performanceSettings.investmentAllocation)));
            }
        }
        if (performanceSettings.applyBetaAdjustmentToRelativeReturns) {
            items.push(requirementRow("t-apply-beta-adjustment-to-relative-returns", "Beta adjusted relative returns", "True"))
        }
        return requirementsSection(items, "Performance");
    }

    return <Container className="t-portfolio-requirements">
        <TitleContainer><Title>Requirements</Title></TitleContainer>
        <PortfolioDescription portfolioName={portfolioName} description={description}/>
        <div>
            {ideaConstraints()}
            {stockSelection()}
            {timing()}
            {autoCloses(false)}
            {autoCloses(true)}
            {performance()}
        </div>
    </Container>;
};
